<script lang="ts">
	import { goto } from '$app/navigation';
	import { createCombobox, melt, type ComboboxOptionProps } from '@melt-ui/svelte';
	import { debounce } from 'lodash-es';
	import { Search, XCircle } from 'lucide-svelte';
	import { writable } from 'svelte/store';
	import { fly } from 'svelte/transition';

	interface Security {
		type: 'search' | 'symbol' | 'mutual-fund';
		name: string;
		ticker?: string;
	}

	const toOption = (security: Security): ComboboxOptionProps<Security> => ({
		value: security,
		label: security.name
	});

	const filteredSecurities = writable<Security[]>([]);

	const {
		elements: { menu, input, option },
		states: { open, inputValue, touchedInput, selected }
	} = createCombobox<Security>({
		onSelectedChange: ({ next }) => {
			if (next) {
				if (next.value.type === 'search') {
					window.location.href = `/search?q=${next.value.name}`;
				} else {
					void goto(`/quote/${next.value.ticker}`);
				}
			}
			return next;
		}
	});

	const loadOptions = debounce(async (search: string) => {
		if (search.length < 2) {
			$filteredSecurities = [];
			return;
		}
		const tags = await fetch(`/api/securities?search=${search}`).then(
			(r) =>
				r.json() as unknown as {
					type: Security['type'];
					exchangeTicker: string;
					securityName: string;
				}[]
		);

		$filteredSecurities = [
			{
				type: 'search',
				name: search
			},
			...tags.map((t) => ({
				type: t.type,
				name: t.securityName,
				ticker: t.exchangeTicker
			}))
		];
	}, 300);

	function formatLabel(label: string) {
		return label.replaceAll(
			new RegExp(`(${$inputValue})`, 'gi'),
			`<span class="font-medium">$1</span>`
		);
	}

	$effect(() => {
		if (!$open) {
			$inputValue = $selected?.label ?? '';
		}
	});

	$effect(() => {
		if ($touchedInput) {
			void loadOptions($inputValue);
		}
	});
</script>

<div class="relative">
	<input
		use:melt={$input}
		class="border-gray-mid flex h-12 w-full justify-between rounded-full border-1 px-4 pr-12 overflow-ellipsis"
		placeholder="Search site eg. AAPL, T-bill"
	/>
	<div class="absolute top-1/2 right-4 z-10 -translate-y-1/2 text-black">
		{#if $selected}
			<button onclick={() => ($selected = undefined)} class="block">
				<XCircle class="text-white-pure w-6 fill-black text-lg" />
			</button>
		{:else}
			<Search class="w-6" />
		{/if}
	</div>
</div>
{#if $open}
	<ul
		class="bg-white-pure z-30 flex max-h-[300px] flex-col overflow-hidden rounded-2xl shadow-md"
		use:melt={$menu}
		transition:fly={{ duration: 150, y: -5 }}
	>
		<!-- svelte-ignore a11y_no_noninteractive_tabindex -->
		<div class="flex max-h-full flex-col gap-0 overflow-y-auto text-black" tabindex="0">
			{#each $filteredSecurities as security, index (index)}
				<li
					use:melt={$option(toOption(security))}
					class="data-[selected]:bg-green-primary data-[selected]:text-white-pure relative cursor-pointer scroll-my-2 py-2 pr-4 pl-4 data-[highlighted]:bg-[#045040]/[0.07]"
				>
					<div class="pl-4">
						{@html formatLabel(security.name)}
						{#if security.ticker && security.type === 'symbol'}
							<span class="block text-sm opacity-75"
								>{@html formatLabel(`SGX: ${security.ticker.replace(/\.SI$/, '')}`)}</span
							>
						{/if}
					</div>
				</li>
			{/each}
		</div>
	</ul>
{/if}
